<template>
  <Dialog
    :visible="visible"
    modal
    :closable="false"
    :style="{ width: '460px' }"
    :pt="{ header: { style: 'padding-bottom: 0' } }"
  >
    <template #header>
      <div class="onboarding-header">
        <span class="onboarding-icon">🎭</span>
        <span class="onboarding-title">{{ $t('onboarding.title') }}</span>
      </div>
    </template>

    <p class="onboarding-body">{{ $t('onboarding.body') }}</p>

    <div v-for="(_, i) in emails" :key="i" class="onboarding-field">
      <div class="onboarding-input-row">
        <InputText
          v-model="emails[i]"
          type="email"
          :placeholder="$t('account.emailPlaceholder')"
          :invalid="!!inputErrors[i]"
          :autofocus="i === 0"
          style="flex: 1"
          @keyup.enter="submit"
        />
        <Button
          v-if="emails.length > 1"
          icon="pi pi-times"
          text
          severity="secondary"
          :aria-label="$t('common.remove')"
          @click="removeEmail(i)"
        />
      </div>
      <small v-if="inputErrors[i]" class="onboarding-error">{{ inputErrors[i] }}</small>
    </div>

    <Button
      :label="$t('onboarding.addAnotherEmail')"
      icon="pi pi-plus"
      text
      size="small"
      class="onboarding-add-btn"
      @click="addEmail"
    />

    <template #footer>
      <div class="onboarding-footer">
        <Button
          :label="$t('onboarding.skip')"
          text
          severity="secondary"
          @click="$emit('close')"
        />
        <Button
          :label="$t('onboarding.save')"
          icon="pi pi-check"
          :loading="saving"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import client from '@/api/client'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'

defineProps({ visible: Boolean })
const emit = defineEmits(['close'])

const { t } = useI18n()
const auth = useAuthStore()

const emails = ref([''])
const inputErrors = ref([''])
const saving = ref(false)

function addEmail() {
  emails.value.push('')
  inputErrors.value.push('')
}

function removeEmail(i) {
  emails.value.splice(i, 1)
  inputErrors.value.splice(i, 1)
}

async function submit() {
  inputErrors.value = emails.value.map(() => '')

  const toSave = emails.value
    .map((e, i) => ({ email: e.trim(), i }))
    .filter(({ email }) => email.length > 0)

  if (toSave.length === 0) {
    inputErrors.value[0] = t('onboarding.emailRequired')
    return
  }

  saving.value = true
  try {
    await Promise.all(
      toSave.map(({ email, i }) =>
        client.post('/account/emails', { email, reminderOptIn: true }).catch(e => {
          inputErrors.value[i] = e.response?.data?.detail || t('common.error')
          throw e
        })
      )
    )
    if (auth.user) auth.user.hasEmails = true
    emit('close')
  } catch {
    // per-field errors already set above
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.onboarding-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.onboarding-icon {
  font-size: 1.5rem;
  line-height: 1;
}

.onboarding-title {
  font-size: 1.1rem;
  font-weight: 700;
}

.onboarding-body {
  margin: 0 0 1rem;
  color: var(--p-text-muted-color);
  line-height: 1.5;
}

.onboarding-field {
  margin-bottom: 0.5rem;
}

.onboarding-input-row {
  display: flex;
  gap: 0.5rem;
}

.onboarding-error {
  display: block;
  margin-top: 0.25rem;
  color: var(--p-red-500, #ef4444);
}

.onboarding-add-btn {
  margin-top: 0.25rem;
  padding-left: 0;
}

.onboarding-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
